<template>
  <v-card class="mx-auto" min-width="500">
    <v-card-title class="text-h6 font-weight-regular justify-space-between">
      <span>{{ pageTitle }}</span>
      <v-avatar color="primary lighten-3" class="subheading white--text" size="28">
        {{ step + 1 }}
      </v-avatar>
    </v-card-title>

    <v-dialog v-model="selectAccountDialog" max-width="400">
      <v-card class="ma-4" max-width="350" v-bind:key="account.id" v-for="account in selectAccountDialogEntries">
        <v-card-title class="text-wrap">
          <p>{{ account.name }}</p>
        </v-card-title>
        <v-card-subtitle>{{ `ΑΦΜ: ${account.tax_registration_number || '-'}` }}</v-card-subtitle>
        <v-card-text>
          <v-container>
            <v-row><div>{{ account.city }} {{ account.zipcode }}</div></v-row>
            <v-row><div>{{ account.address }}</div></v-row>
            <v-row><div>{{ account.telephone }}</div></v-row>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="primary" @click="updateAccount('supplier', account); closeSelectAccountDialog();">ΕΠΙΛΟΓΗ</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-window v-model="step">
      <!-- STEP 0: ΒΑΣΙΚΑ ΣΤΟΙΧΕΙΑ ΠΑΡΑΣΤΑΤΙΚΟΥ -->
      <v-window-item :value="0">
        <v-card-text>
          <h3 class="text-h6 font-weight-light mb-4">Βασικά στοιχεία παραστατικού</h3>

          <v-select
            :items="documentTypes"
            v-model="selectedDocumentType"
            return-object
            item-text="description"
            label="Είδος παραστατικού"
            :disabled="isEditMode"
            :error-messages="allerrors.document_type_id">
          </v-select>

          <v-select
            :items="filteredDocumentSeries"
            v-model="selectedSeries"
            return-object
            item-text="name"
            label="Σειρά"
            :disabled="isEditMode"
            :error-messages="allerrors.document_series_id">
          </v-select>

          <v-text-field
            v-model.number="document_number"
            label="Αριθμός παραστατικού"
            :disabled="isEditMode || selectedSeries.autonumbered"
            :hint="selectedSeries.autonumbered ? (isEditMode ? 'Κλειδωμένη αρίθμηση' : 'Αυτόματη αρίθμηση κατά την έκδοση') : 'Χειροκίνητη αρίθμηση'"
            persistent-hint>
          </v-text-field>

          <v-select
            :items="warehouses"
            v-model="selectedWarehouse"
            return-object
            item-text="name"
            item-value="id"
            label="Αποθήκη"
            :error-messages="allerrors.warehouse_id">
          </v-select>

          <v-menu
            v-model="menuDate"
            :close-on-content-click="false"
            :nudge-right="40"
            transition="scale-transition"
            offset-y
            min-width="auto">
            <template v-slot:activator="{ on, attrs }">
              <v-text-field
                v-model="document_issue_date"
                label="Ημερομηνία έκδοσης"
                append-icon="mdi-calendar"
                readonly
                v-bind="attrs"
                v-on="on"
                :error-messages="allerrors.document_issue_date">
              </v-text-field>
            </template>
            <v-date-picker v-model="document_issue_date" @input="menuDate = false"></v-date-picker>
          </v-menu>
        </v-card-text>
      </v-window-item>

      <!-- STEP 1: ΣΤΟΙΧΕΙΑ ΔΙΚΑΙΟΥΧΟΥ / ΠΡΟΜΗΘΕΥΤΗ -->
      <v-window-item :value="1">
        <v-card-text>
          <v-row>
            <h3 class="text-h6 font-weight-light mb-2">Στοιχεία Δικαιούχου (Μη υπόχρεου εκδότη)</h3>
            <v-spacer></v-spacer>
            <v-btn text color="warning" @click="clearSupplierFields">ΚΑΘΑΡΙΣΜΑ</v-btn>
          </v-row>

          <v-text-field
            v-model="supplier.name"
            label="Ονοματεπώνυμο / Επωνυμία *"
            append-icon="mdi-account-search"
            @click:append="searchAccountByName('supplier')"
            :error-messages="allerrors['supplier.name']">
          </v-text-field>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="supplier.tax_registration_number"
                label="ΑΦΜ *"
                append-icon="mdi-account-search"
                @click:append="searchAccountByTaxRegistrationNumber('supplier')"
                :loading="disabledTaxRegistrationNumberFields.supplier"
                :rules="[v => !!v || 'Το ΑΦΜ είναι υποχρεωτικό για την έκδοση Τίτλου Κτήσης']"
                :error-messages="allerrors['supplier.tax_registration_number']">
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="supplier.tax_registration_office_id"
                :items="taxOffices"
                item-text="name"
                item-value="id"
                label="ΔΟΥ">
              </v-select>
            </v-col>
          </v-row>

          <v-text-field
            v-model="supplier.jobtitle"
            label="Ιδιότητα / Επάγγελμα">
          </v-text-field>

          <v-row>
            <v-col cols="12" sm="6">
              <v-select
                v-model="supplier.country_id"
                :items="countries"
                item-text="name_gr"
                item-value="id"
                label="Χώρα">
              </v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="supplier.city"
                label="Πόλη">
              </v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="8">
              <v-text-field
                v-model="supplier.address"
                label="Διεύθυνση">
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="4">
              <v-text-field
                v-model="supplier.zipcode"
                label="ΤΚ">
              </v-text-field>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="supplier.telephone"
                label="Τηλέφωνο">
              </v-text-field>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="supplier.email"
                label="E-Mail">
              </v-text-field>
            </v-col>
          </v-row>
        </v-card-text>
      </v-window-item>

      <!-- STEP 2: ΓΡΑΜΜΕΣ ΑΓΑΘΩΝ / ΥΠΗΡΕΣΙΩΝ -->
      <v-window-item :value="2">
        <v-card-text>
          <v-data-table no-data-text="Δεν υπάρχουν δεδομένα"
            :headers="itemsHeaders"
            :items="items"
            class="elevation-1"
            disable-pagination
            disable-filtering
            hide-default-footer>
            <template v-slot:top>
              <v-toolbar flat>
                <v-toolbar-title>Αγαθά / Υπηρεσίες Τίτλου</v-toolbar-title>
                <v-divider class="mx-4" inset vertical></v-divider>
                <span>Σύνολο γραμμών: {{ items.length }}</span>
                <v-spacer></v-spacer>

                <!-- Dialog Add/Edit Line -->
                <v-dialog v-model="dialogLine" max-width="600px">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn color="primary" dark class="mb-2" v-bind="attrs" v-on="on" @click="newLineClicked">
                      <v-icon left>mdi-plus</v-icon> ΠΡΟΣΘΗΚΗ
                    </v-btn>
                  </template>
                  <v-card>
                    <v-card-title>
                      <span class="text-h5">{{ formTitle }}</span>
                    </v-card-title>
                    <v-card-text>
                      <v-container>
                        <v-checkbox
                          label="Είδος εκτός καταλόγου / Παροχή υπηρεσίας"
                          v-model="editedItem.isAdHoc"
                          @change="adHocChanged">
                        </v-checkbox>

                        <v-autocomplete
                          v-if="!editedItem.isAdHoc"
                          :items="products"
                          v-model="editedItem.product_id"
                          item-value="id"
                          item-text="text"
                          label="Επιλογή είδους αποθήκης"
                          @change="productSelected">
                        </v-autocomplete>

                        <v-text-field
                          label="Περιγραφή είδους / υπηρεσίας *"
                          v-model="editedItem.override_product_description">
                        </v-text-field>

                        <v-row>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              label="Ποσότητα *"
                              v-model.number="editedItem.quantity"
                              type="number"
                              min="0"
                              step="any">
                            </v-text-field>
                          </v-col>
                          <v-col cols="12" sm="6">
                            <vuetify-money
                              v-model="editedItem.unit_price"
                              label="Τιμή μονάδας *"
                              :options="moneyFieldOptions">
                            </vuetify-money>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" sm="6">
                            <v-text-field
                              label="Έκπτωση %"
                              v-model.number="editedItem.discount_percentage"
                              type="number"
                              min="0"
                              max="100">
                            </v-text-field>
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-select
                              :items="measurementUnits"
                              v-model="editedItem.override_product_measurement_unit_id"
                              item-value="id"
                              item-text="description"
                              label="Μονάδα μέτρησης">
                            </v-select>
                          </v-col>
                        </v-row>

                        <v-row>
                          <v-col cols="12" sm="6">
                            <v-select
                              :items="availableExpenseCategories"
                              v-model="editedItem.expense_classification_category"
                              label="Κατηγορία Εξόδου (myDATA)"
                              @change="onCategoryChanged">
                            </v-select>
                          </v-col>
                          <v-col cols="12" sm="6">
                            <v-select
                              :items="availableExpenseClassificationTypes"
                              v-model="editedItem.expense_classification_type"
                              label="Χαρακτηρισμός Ε3 (myDATA)">
                            </v-select>
                          </v-col>
                        </v-row>
                      </v-container>
                    </v-card-text>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="warning darken-1" text @click="closeLineDialog">ΑΚΥΡΟ</v-btn>
                      <v-btn color="success darken-1" text @click="saveLine" :disabled="!isLineValid">ΑΠΟΘΗΚΕΥΣΗ</v-btn>
                    </v-card-actions>
                  </v-card>
                </v-dialog>

                <!-- Dialog Delete Line -->
                <v-dialog v-model="dialogDeleteLine" max-width="400px">
                  <v-card>
                    <v-card-title class="text-h6">Διαγραφή γραμμής;</v-card-title>
                    <v-card-actions>
                      <v-spacer></v-spacer>
                      <v-btn color="primary text" text @click="dialogDeleteLine = false">ΑΚΥΡΟ</v-btn>
                      <v-btn color="error darken-1" @click="deleteLineConfirm">ΔΙΑΓΡΑΦΗ</v-btn>
                      <v-spacer></v-spacer>
                    </v-card-actions>
                  </v-card>
                </v-dialog>
              </v-toolbar>
            </template>

            <template v-slot:[`item.unit_price`]="{ item }">
              {{ formatCurrency(item.unit_price) }}
            </template>

            <template v-slot:[`item.net_value`]="{ item }">
              {{ formatCurrency(item.total_price - item.discount) }}
            </template>

            <template v-slot:[`item.actions`]="{ item }">
              <v-icon small class="mr-2" color="primary" @click="editLine(item)">mdi-pencil</v-icon>
              <v-icon small color="error" @click="deleteLine(item)">mdi-delete</v-icon>
            </template>
          </v-data-table>
        </v-card-text>
      </v-window-item>

      <!-- STEP 3: ΠΑΡΑΚΡΑΤΗΣΕΙΣ & ΕΠΙΒΑΡΥΝΣΕΙΣ -->
      <v-window-item :value="3">
        <v-card-text>
          <h3 class="text-h6 font-weight-light mb-4">Παρακρατήσεις Φόρου & Ψηφιακό Τέλος</h3>

          <v-card outlined class="pa-4 mb-4">
            <v-subheader class="font-weight-bold">1. Παρακράτηση Φόρου Εισοδήματος (AADE Withholding Tax)</v-subheader>
            <v-radio-group v-model="taxWithholdingPreset" row @change="withholdingPresetChanged">
              <v-radio label="0% (Αγορά αγαθών από ιδιώτη)" value="0"></v-radio>
              <v-radio label="20% (Παροχή υπηρεσιών από μη υπόχρεο)" value="20"></v-radio>
              <v-radio label="Προσαρμοσμένο %" value="custom"></v-radio>
            </v-radio-group>
            <v-row v-if="taxWithholdingPreset === 'custom'">
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model.number="tax_withholding_percentage"
                  label="Ποσοστό παρακράτησης %"
                  type="number"
                  min="0"
                  max="100"
                  step="any">
                </v-text-field>
              </v-col>
            </v-row>
            <div class="subtitle-2 primary--text">
              Ποσό Παρακράτησης: {{ formatCurrency(calculatedTaxWithholdingAmount) }}
            </div>
          </v-card>

          <v-card outlined class="pa-4 mb-4">
            <v-subheader class="font-weight-bold">2. Ψηφιακό Τέλος Συναλλαγής / Χαρτόσημο (Stamp Duty)</v-subheader>
            <v-radio-group v-model="stampDutyPreset" row @change="stampDutyPresetChanged">
              <v-radio label="0% (Χωρίς τέλος)" value="0"></v-radio>
              <v-radio label="2.4% (2% + 20% ΟΓΑ)" value="2.4"></v-radio>
              <v-radio label="3.6% (3% + 20% ΟΓΑ)" value="3.6"></v-radio>
              <v-radio label="Προσαρμοσμένο %" value="custom"></v-radio>
            </v-radio-group>
            <v-row v-if="stampDutyPreset === 'custom'">
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model.number="stamp_duty_percentage"
                  label="Ποσοστό τέλους %"
                  type="number"
                  min="0"
                  max="100"
                  step="any">
                </v-text-field>
              </v-col>
            </v-row>
            <div class="subtitle-2 primary--text">
              Ποσό Ψηφιακού Τέλους: {{ formatCurrency(calculatedStampDutyAmount) }}
            </div>
          </v-card>

          <v-card outlined class="pa-4 mb-4">
            <v-subheader class="font-weight-bold">3. Κράτηση ΕΦΚΑ (εφόσον συντρέχει υποχρέωση)</v-subheader>
            <v-row>
              <v-col cols="12" sm="4">
                <v-text-field
                  v-model.number="efka_percentage"
                  label="Ποσοστό ΕΦΚΑ %"
                  type="number"
                  min="0"
                  max="100"
                  step="any">
                </v-text-field>
              </v-col>
            </v-row>
            <div class="subtitle-2 primary--text">
              Ποσό ΕΦΚΑ: {{ formatCurrency(calculatedEfkaAmount) }}
            </div>
          </v-card>

          <v-select
            :items="paymentTypes"
            v-model="selectedPaymentType"
            return-object
            item-text="description"
            label="Τρόπος πληρωμής">
          </v-select>

          <v-textarea
            v-model="comments"
            label="Παρατηρήσεις / Σχόλια"
            rows="2">
          </v-textarea>
        </v-card-text>
      </v-window-item>

      <!-- STEP 4: ΣΥΝΟΨΗ & ΕΚΔΟΣΗ -->
      <v-window-item :value="4">
        <v-card-text>
          <h3 class="text-h6 font-weight-light mb-4">Σύνοψη & Έκδοση Τίτλου Κτήσης</h3>

          <v-card outlined class="pa-4 mb-4">
            <v-row dense>
              <v-col cols="12" sm="6"><strong>Είδος:</strong> {{ selectedDocumentType.description }}</v-col>
              <v-col cols="12" sm="6"><strong>Σειρά:</strong> {{ selectedSeries.name || '-' }}</v-col>
              <v-col cols="12" sm="6"><strong>Ημερομηνία:</strong> {{ document_issue_date }}</v-col>
              <v-col cols="12" sm="6"><strong>Δικαιούχος:</strong> {{ supplier.name }} (ΑΦΜ: {{ supplier.tax_registration_number || '-' }})</v-col>
            </v-row>
          </v-card>

          <v-card class="pa-4 grey lighten-4">
            <v-row justify="space-between" class="my-1">
              <span>Ακαθάριστη / Καθαρή Αξία:</span>
              <span class="font-weight-bold">{{ formatCurrency(totalNetValue) }}</span>
            </v-row>
            <v-row justify="space-between" class="my-1 red--text text--darken-2">
              <span>- Παρακράτηση Φόρου ({{ tax_withholding_percentage }}%):</span>
              <span class="font-weight-bold">- {{ formatCurrency(calculatedTaxWithholdingAmount) }}</span>
            </v-row>
            <v-row justify="space-between" class="my-1 red--text text--darken-2">
              <span>- Ψηφιακό Τέλος / Χαρτόσημο ({{ stamp_duty_percentage }}%):</span>
              <span class="font-weight-bold">- {{ formatCurrency(calculatedStampDutyAmount) }}</span>
            </v-row>
            <v-row justify="space-between" class="my-1 red--text text--darken-2" v-if="efka_percentage > 0">
              <span>- Κράτηση ΕΦΚΑ ({{ efka_percentage }}%):</span>
              <span class="font-weight-bold">- {{ formatCurrency(calculatedEfkaAmount) }}</span>
            </v-row>
            <v-divider class="my-3"></v-divider>
            <v-row justify="space-between" class="text-h6 green--text text--darken-3 font-weight-bold">
              <span>ΠΛΗΡΩΤΕΟ ΠΟΣΟ:</span>
              <span>{{ formatCurrency(totalPayableAmount) }}</span>
            </v-row>
          </v-card>

          <div class="text-center mt-6">
            <v-btn
              color="success"
              large
              @click="saveDocument"
              :loading="saveDocumentButtonDisabled">
              <v-icon left>mdi-check-circle</v-icon>
              {{ isEditMode ? 'ΕΝΗΜΕΡΩΣΗ & ΑΠΟΘΗΚΕΥΣΗ' : 'ΕΚΔΟΣΗ & ΑΠΟΘΗΚΕΥΣΗ' }}
            </v-btn>
          </div>
        </v-card-text>
      </v-window-item>
    </v-window>

    <v-card-actions>
      <v-btn
        :disabled="step <= 0"
        text
        @click="step--"
        color="primary">
        ΠΙΣΩ
      </v-btn>
      <v-spacer></v-spacer>
      <v-item-group v-model="step" class="text-center" mandatory>
        <v-item v-for="n in 5" :key="`btn-${n}`" v-slot="{ active, toggle }">
          <v-btn :input-value="active" icon dense x-small disabled @click="toggle">
            <v-icon>mdi-record</v-icon>
          </v-btn>
        </v-item>
      </v-item-group>
      <v-spacer></v-spacer>
      <v-btn
        :disabled="!canProceedNextStep"
        color="primary"
        @click="step++">
        ΕΠΟΜΕΝΟ
      </v-btn>
    </v-card-actions>
  </v-card>
</template>

<script>
import axios from "axios";
import currency from "currency.js";

const EXPENSE_CATEGORIES = [
  { text: "category2_3 - Λήψη Υπηρεσιών", value: "category2_3" },
  { text: "category2_5 - Γενικά Έξοδα χωρίς δικαίωμα έκπτωσης", value: "category2_5" },
  { text: "category2_1 - Αγορές εμπορευμάτων", value: "category2_1" },
  { text: "category2_2 - Αγορές πρώτων υλών", value: "category2_2" },
  { text: "category2_7 - Αγορές παγίων", value: "category2_7" },
];

const CLASSIFICATION_OPTIONS_3_1 = {
  category2_3: [
    { text: "E3_585_009 - Λοιπές Αμοιβές για υπηρεσίες ημεδαπής", value: "E3_585_009" },
    { text: "E3_585_016 - Λοιπά έξοδα", value: "E3_585_016" },
    { text: "E3_585_004 - Δαπάνες για ενημερωτικές ημερίδες", value: "E3_585_004" },
  ],
  category2_5: [
    { text: "E3_585_016 - Λοιπά έξοδα", value: "E3_585_016" },
    { text: "E3_585_004 - Δαπάνες για ενημερωτικές ημερίδες", value: "E3_585_004" },
  ],
  category2_1: [
    { text: "E3_102_006 - Αγορές εμπορευμάτων χρήσης / Λοιπά", value: "E3_102_006" },
    { text: "E3_102_002 - Αγορές εμπορευμάτων χρήσης / Λιανικές", value: "E3_102_002" },
  ],
  category2_2: [
    { text: "E3_202_005 - Αγορές πρώτων υλών και υλικών χρήσης / Λοιπά", value: "E3_202_005" },
    { text: "E3_202_002 - Αγορές πρώτων υλών και υλικών χρήσης / Λιανικές", value: "E3_202_002" },
  ],
  category2_7: [
    { text: "E3_882_002 - Αγορές ενσώματων παγίων χρήσης / Λιανικές", value: "E3_882_002" },
    { text: "E3_883_002 - Αγορές μη ενσώματων παγίων χρήσης / Λιανικές", value: "E3_883_002" },
  ],
};

const CLASSIFICATION_OPTIONS_3_2 = {
  category2_3: [
    { text: "E3_585_009 - Λοιπές Αμοιβές για υπηρεσίες ημεδαπής", value: "E3_585_009" },
    { text: "E3_585_016 - Λοιπά έξοδα", value: "E3_585_016" },
    { text: "E3_585_004 - Δαπάνες για ενημερωτικές ημερίδες", value: "E3_585_004" },
  ],
  category2_5: [
    { text: "E3_585_016 - Λοιπά έξοδα", value: "E3_585_016" },
    { text: "E3_585_009 - Λοιπές Αμοιβές για υπηρεσίες ημεδαπής", value: "E3_585_009" },
    { text: "E3_585_004 - Δαπάνες για ενημερωτικές ημερίδες", value: "E3_585_004" },
  ],
  category2_1: [
    { text: "E3_102_001 - Αγορές εμπορευμάτων χρήσης / Χονδρικές", value: "E3_102_001" },
    { text: "E3_102_002 - Αγορές εμπορευμάτων χρήσης / Λιανικές", value: "E3_102_002" },
    { text: "E3_102_006 - Αγορές εμπορευμάτων χρήσης / Λοιπά", value: "E3_102_006" },
  ],
  category2_2: [
    { text: "E3_202_001 - Αγορές πρώτων υλών και υλικών χρήσης / Χονδρικές", value: "E3_202_001" },
    { text: "E3_202_002 - Αγορές πρώτων υλών και υλικών χρήσης / Λιανικές", value: "E3_202_002" },
    { text: "E3_202_005 - Αγορές πρώτων υλών και υλικών χρήσης / Λοιπά", value: "E3_202_005" },
  ],
  category2_7: [
    { text: "E3_882_001 - Αγορές ενσώματων παγίων χρήσης / Χονδρικές", value: "E3_882_001" },
    { text: "E3_882_002 - Αγορές ενσώματων παγίων χρήσης / Λιανικές", value: "E3_882_002" },
    { text: "E3_883_001 - Αγορές μη ενσώματων παγίων χρήσης / Χονδρικές", value: "E3_883_001" },
    { text: "E3_883_002 - Αγορές μη ενσώματων παγίων χρήσης / Λιανικές", value: "E3_883_002" },
  ],
};

export default {
  name: "NewAcquisitionDocumentPage",
  data: () => ({
    step: 0,
    menuDate: false,
    allerrors: {},
    saveDocumentButtonDisabled: false,

    documentTypes: [],
    selectedDocumentType: {},
    selectedSeries: {},
    document_number: "",
    document_issue_date: "",

    warehouses: [],
    selectedWarehouse: null,

    supplier: {},
    selectAccountDialog: false,
    selectAccountDialogEntries: [],
    disabledTaxRegistrationNumberFields: { supplier: false },

    taxOffices: [],
    countries: [],
    paymentTypes: [],
    selectedPaymentType: null,
    comments: "",

    products: [],
    measurementUnits: [],

    // Deductions
    taxWithholdingPreset: "0",
    tax_withholding_percentage: 0,
    stampDutyPreset: "2.4",
    stamp_duty_percentage: 2.4,
    efka_percentage: 0,

    // Line items
    items: [],
    dialogLine: false,
    dialogDeleteLine: false,
    editedIndex: -1,
    editedItem: {
      isAdHoc: true,
      product_id: null,
      override_product_description: "",
      quantity: 1,
      unit_price: 0,
      discount_percentage: 0,
      override_product_measurement_unit_id: null,
      expense_classification_type: "E3_585_009",
      expense_classification_category: "category2_3",
    },
    defaultItem: {
      isAdHoc: true,
      product_id: null,
      override_product_description: "",
      quantity: 1,
      unit_price: 0,
      discount_percentage: 0,
      override_product_measurement_unit_id: null,
      expense_classification_type: "E3_585_009",
      expense_classification_category: "category2_3",
    },

    moneyFieldOptions: {
      locale: "el-GR",
      prefix: "€ ",
      precision: 2,
    },

    itemsHeaders: [
      { text: "Περιγραφή", value: "override_product_description", align: "start" },
      { text: "Ποσότητα", value: "quantity", align: "end" },
      { text: "Τιμή Μον.", value: "unit_price", align: "end" },
      { text: "Καθαρή Αξία", value: "net_value", align: "end" },
      { text: "Ενέργειες", value: "actions", sortable: false, align: "center" },
    ],
  }),

  computed: {
    formTitle() {
      return this.editedIndex === -1 ? "Προσθήκη Είδους / Υπηρεσίας" : "Επεξεργασία";
    },

    filteredDocumentSeries() {
      return this.selectedDocumentType?.series || [];
    },

    isLineValid() {
      const desc = this.editedItem.override_product_description || (this.editedItem.product?.description ?? "");
      const qty = parseFloat(this.editedItem.quantity);
      const price = parseFloat(this.editedItem.unit_price);
      return desc.trim().length > 0 && !isNaN(qty) && qty > 0 && !isNaN(price) && price >= 0;
    },

    totalNetValue() {
      return this.items.reduce((acc, item) => {
        const net = (item.quantity * item.unit_price) - (item.discount || 0);
        return currency(acc).add(net).value;
      }, 0);
    },

    calculatedTaxWithholdingAmount() {
      if (!this.tax_withholding_percentage || this.tax_withholding_percentage <= 0) return 0;
      return currency(this.totalNetValue).multiply(this.tax_withholding_percentage / 100).value;
    },

    calculatedStampDutyAmount() {
      if (!this.stamp_duty_percentage || this.stamp_duty_percentage <= 0) return 0;
      return currency(this.totalNetValue).multiply(this.stamp_duty_percentage / 100).value;
    },

    calculatedEfkaAmount() {
      if (!this.efka_percentage || this.efka_percentage <= 0) return 0;
      return currency(this.totalNetValue).multiply(this.efka_percentage / 100).value;
    },

    totalPayableAmount() {
      const net = currency(this.totalNetValue);
      const deductions = currency(this.calculatedTaxWithholdingAmount)
        .add(this.calculatedStampDutyAmount)
        .add(this.calculatedEfkaAmount);
      return Math.max(0, net.subtract(deductions).value);
    },

    canProceedNextStep() {
      if (this.step === 0) {
        return !!(this.selectedDocumentType?.id && this.selectedSeries?.id && this.document_issue_date);
      }
      if (this.step === 1) {
        const hasName = !!(this.supplier.name && this.supplier.name.trim().length > 0);
        const hasAfm = !!(this.supplier.tax_registration_number && String(this.supplier.tax_registration_number).trim().length >= 8);
        return hasName && hasAfm;
      }
      if (this.step === 2) {
        return this.items.length > 0;
      }
      if (this.step >= 4) {
        return false;
      }
      return true;
    },

    isEditMode() {
      return !!this.$route.params.id;
    },

    pageTitle() {
      return this.isEditMode
        ? `Επεξεργασία Τίτλου Κτήσης #${this.document_number || ""}`
        : "Έκδοση Τίτλου Κτήσης (Self-Billing)";
    },

    currentAadeCode() {
      return (
        this.selectedDocumentType?.tax_authority_code ||
        this.selectedDocumentType?.classification?.tax_authority_code ||
        "3.1"
      );
    },

    availableExpenseCategories() {
      return EXPENSE_CATEGORIES;
    },

    availableExpenseClassificationTypes() {
      const code = this.currentAadeCode === "3.2" ? "3.2" : "3.1";
      const map = code === "3.2" ? CLASSIFICATION_OPTIONS_3_2 : CLASSIFICATION_OPTIONS_3_1;
      const cat = this.editedItem.expense_classification_category || "category2_3";
      return map[cat] || [];
    },
  },

  watch: {
    selectedDocumentType(val) {
      if (!this.isEditMode && val?.series && val.series.length > 0) {
        this.selectedSeries = val.series[0];
      }
      this.onCategoryChanged();
    },
  },

  created() {
    this.initialize();
  },

  methods: {
    initialize() {
      const dateAthens = new Intl.DateTimeFormat("en-CA", {
        timeZone: "Europe/Athens",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      }).format(new Date());

      this.document_issue_date = dateAthens;

      axios.all([
        axios.get("/api/document-types/acquisition"),
        axios.get("/api/tax-offices"),
        axios.get("/api/countries"),
        axios.get("/api/tax-authority/payment-types"),
        axios.get("/api/products"),
        axios.get("/api/measurement-units"),
        axios.get("/api/warehouses"),
      ]).then(
        axios.spread((docTypesRes, taxOfficesRes, countriesRes, paymentTypesRes, productsRes, unitsRes, warehousesRes) => {
          this.documentTypes = docTypesRes.data;
          if (this.documentTypes.length > 0) {
            this.selectedDocumentType = this.documentTypes[0];
            if (this.selectedDocumentType.series?.length > 0) {
              this.selectedSeries = this.selectedDocumentType.series[0];
            }
          }

          this.taxOffices = taxOfficesRes.data;
          this.countries = countriesRes.data;
          this.paymentTypes = paymentTypesRes.data;
          if (this.paymentTypes.length > 0) {
            this.selectedPaymentType = this.paymentTypes[0];
          }

          this.products = (productsRes.data || []).map((p) => ({
            ...p,
            text: `${p.code} - ${p.description}`,
          }));

          this.measurementUnits = unitsRes.data;
          this.warehouses = warehousesRes.data;
          if (this.warehouses.length > 0) {
            this.selectedWarehouse = this.warehouses[0];
          }

          if (this.isEditMode) {
            this.loadExistingDocument(this.$route.params.id);
          }
        })
      ).catch((err) => console.error(err));
    },

    loadExistingDocument(id) {
      axios.get(`/api/documents/acquisition/${id}`)
        .then((response) => {
          const doc = response.data;
          if (doc.invoice_mark) {
            if (this.$swal) {
              this.$swal({
                icon: "warning",
                title: "Μη επιτρεπτή ενέργεια",
                text: "Το παραστατικό έχει ήδη διαβιβαστεί στην ΑΑΔΕ (Mark: " + doc.invoice_mark + ") και δεν επιτρέπεται η επεξεργασία του.",
              });
            }
            this.$router.push({ name: "acquisitiondocuments" });
            return;
          }
          if (doc.is_cancelled) {
            if (this.$swal) {
              this.$swal({
                icon: "warning",
                title: "Μη επιτρεπτή ενέργεια",
                text: "Το παραστατικό έχει ακυρωθεί και δεν μπορεί να τροποποιηθεί.",
              });
            }
            this.$router.push({ name: "acquisitiondocuments" });
            return;
          }

          this.document_number = doc.document_number;
          this.document_issue_date = doc.issue_date;

          const matchedType = this.documentTypes.find((t) => t.id === doc.document_type_id);
          if (matchedType) {
            this.selectedDocumentType = matchedType;
            if (matchedType.series?.length > 0) {
              this.selectedSeries = matchedType.series.find((s) => s.id === doc.document_series_id) || matchedType.series[0];
            }
          }

          if (doc.warehouse_id) {
            this.selectedWarehouse = this.warehouses.find((w) => w.id === doc.warehouse_id) || null;
          }
          if (doc.payment_type_id) {
            this.selectedPaymentType = this.paymentTypes.find((p) => p.id === doc.payment_type_id) || null;
          }

          this.comments = doc.comments || "";

          this.tax_withholding_percentage = parseFloat(doc.tax_withholding_percentage) || 0;
          if (this.tax_withholding_percentage === 0) this.taxWithholdingPreset = "0";
          else if (this.tax_withholding_percentage === 20) this.taxWithholdingPreset = "20";
          else this.taxWithholdingPreset = "custom";

          this.stamp_duty_percentage = parseFloat(doc.stamp_duty_percentage) || 0;
          if (this.stamp_duty_percentage === 0) this.stampDutyPreset = "0";
          else if (this.stamp_duty_percentage === 2.4) this.stampDutyPreset = "2.4";
          else if (this.stamp_duty_percentage === 3.6) this.stampDutyPreset = "3.6";
          else this.stampDutyPreset = "custom";

          this.efka_percentage = parseFloat(doc.efka_percentage) || 0;

          if (doc.supplier) {
            this.supplier = {
              id: doc.supplier.id,
              name: doc.supplier.name || "",
              trade_name: doc.supplier.trade_name || null,
              tax_registration_number: doc.supplier.tax_registration_number || "",
              tax_registration_office_id: doc.supplier.tax_registration_office_id || null,
              country_id: doc.supplier.country_id || 1,
              city: doc.supplier.city || "",
              address: doc.supplier.address || "",
              zipcode: doc.supplier.zipcode || "",
              telephone: doc.supplier.telephone || "",
              fax: doc.supplier.fax || "",
              mobile: doc.supplier.mobile || "",
              email: doc.supplier.email || "",
              jobtitle: doc.supplier.jobtitle || "",
            };
          }

          if (doc.lines && doc.lines.length > 0) {
            this.items = doc.lines.map((line) => {
              const qty = parseFloat(line.quantity) || 1;
              const price = parseFloat(line.unit_price) || 0;
              const discPct = parseFloat(line.discount_percentage) || 0;
              const discVal = parseFloat(line.discount_value) || 0;
              const net = parseFloat(line.net_value) || (qty * price - discVal);

              return {
                isAdHoc: !line.product_id,
                product_id: line.product_id || null,
                override_product_description: line.override_product_description || line.product?.description || "",
                override_product_measurement_unit_id: line.override_product_measurement_unit_id || null,
                quantity: qty,
                unit_price: price,
                discount_percentage: discPct,
                discount: discVal,
                total_price: net + discVal,
                expense_classification_category: line.expense_category?.tax_authority_code || "category2_3",
                expense_classification_type: line.expense_classification?.tax_authority_code || "E3_585_009",
              };
            });
          }
        })
        .catch((err) => {
          console.error(err);
          if (this.$swal) {
            this.$swal({
              icon: "error",
              title: "Σφάλμα",
              text: "Δεν ήταν δυνατή η φόρτωση του παραστατικού.",
            });
          }
          this.$router.push({ name: "acquisitiondocuments" });
        });
    },

    formatCurrency(val) {
      return currency(val, { symbol: "€ ", separator: ".", decimal: "," }).format();
    },

    clearSupplierFields() {
      this.supplier = {};
    },

    withholdingPresetChanged(val) {
      if (val === "0") this.tax_withholding_percentage = 0;
      else if (val === "20") this.tax_withholding_percentage = 20;
    },

    stampDutyPresetChanged(val) {
      if (val === "0") this.stamp_duty_percentage = 0;
      else if (val === "2.4") this.stamp_duty_percentage = 2.4;
      else if (val === "3.6") this.stamp_duty_percentage = 3.6;
    },

    onCategoryChanged() {
      const types = this.availableExpenseClassificationTypes;
      const valid = types.some((t) => t.value === this.editedItem.expense_classification_type);
      if (!valid && types.length > 0) {
        this.editedItem.expense_classification_type = types[0].value;
      }
    },

    newLineClicked() {
      this.editedIndex = -1;
      this.editedItem = Object.assign({}, this.defaultItem);
      this.onCategoryChanged();
      this.dialogLine = true;
    },

    adHocChanged(isAdHoc) {
      if (isAdHoc) {
        this.editedItem.product_id = null;
      }
    },

    productSelected(productId) {
      const prod = this.products.find((p) => p.id === productId);
      if (prod) {
        this.editedItem.override_product_description = prod.description;
        this.editedItem.unit_price = prod.last_purchase_unit_price || prod.purchase_price || 0;
        this.editedItem.override_product_measurement_unit_id = prod.measurement_unit_id;
      }
    },

    editLine(item) {
      this.editedIndex = this.items.indexOf(item);
      this.editedItem = Object.assign({}, item);
      this.onCategoryChanged();
      this.dialogLine = true;
    },

    deleteLine(item) {
      this.editedIndex = this.items.indexOf(item);
      this.dialogDeleteLine = true;
    },

    deleteLineConfirm() {
      this.items.splice(this.editedIndex, 1);
      this.dialogDeleteLine = false;
      this.editedIndex = -1;
    },

    closeLineDialog() {
      this.dialogLine = false;
      this.editedIndex = -1;
    },

    saveLine() {
      const qty = parseFloat(this.editedItem.quantity) || 1;
      const price = parseFloat(this.editedItem.unit_price) || 0;
      const discPct = parseFloat(this.editedItem.discount_percentage) || 0;
      const totalPrice = currency(qty).multiply(price).value;
      const discount = discPct > 0 ? currency(totalPrice).multiply(discPct / 100).value : 0;

      const lineData = {
        ...this.editedItem,
        quantity: qty,
        unit_price: price,
        discount_percentage: discPct,
        total_price: totalPrice,
        discount: discount,
      };

      if (this.editedIndex > -1) {
        this.$set(this.items, this.editedIndex, lineData);
      } else {
        this.items.push(lineData);
      }

      this.closeLineDialog();
    },

    async searchAccountByTaxRegistrationNumber(target) {
      const afm = this[target].tax_registration_number;
      if (!afm || afm.length < 8) return;

      this.disabledTaxRegistrationNumberFields[target] = true;
      try {
        const response = await axios.get(`/api/accounts/search/tax-registration-number/${encodeURIComponent(afm)}`);
        if (response.data.length === 1) {
          this.updateAccount(target, response.data[0]);
        } else if (response.data.length > 1) {
          this.selectAccountDialogEntries = response.data;
          this.selectAccountDialog = true;
        }
      } catch (err) {
        console.error(err);
      } finally {
        this.disabledTaxRegistrationNumberFields[target] = false;
      }
    },

    async searchAccountByName(target) {
      const name = this[target].name;
      if (!name || name.length < 2) return;

      try {
        const response = await axios.get(`/api/accounts/search/name/${encodeURIComponent(name)}`);
        if (response.data.length === 1) {
          this.updateAccount(target, response.data[0]);
        } else if (response.data.length > 1) {
          this.selectAccountDialogEntries = response.data;
          this.selectAccountDialog = true;
        }
      } catch (err) {
        console.error(err);
      }
    },

    updateAccount(target, account) {
      this[target] = {
        id: account.id || null,
        name: account.name || "",
        trade_name: account.trade_name || null,
        tax_registration_number: account.tax_registration_number || "",
        tax_registration_office_id: account.tax_registration_office_id || null,
        country_id: account.country_id || 1,
        city: account.city || "",
        address: account.address || "",
        zipcode: account.zipcode || "",
        telephone: account.telephone || "",
        fax: account.fax || "",
        mobile: account.mobile || "",
        email: account.email || "",
        jobtitle: account.jobtitle || "",
      };
    },

    closeSelectAccountDialog() {
      this.selectAccountDialog = false;
    },

    saveDocument() {
      if (!this.supplier.tax_registration_number || String(this.supplier.tax_registration_number).trim().length < 8) {
        if (this.$swal) {
          this.$swal({
            icon: "warning",
            title: "Ελλιπή στοιχεία",
            text: "Το ΑΦΜ του δικαιούχου είναι υποχρεωτικό για την έκδοση Τίτλου Κτήσης.",
          });
        }
        this.step = 1;
        return;
      }

      this.saveDocumentButtonDisabled = true;
      this.allerrors = {};

      const requestBody = {
        document_type_id: this.selectedDocumentType.id,
        document_series_id: this.selectedSeries.id,
        document_number: this.selectedSeries.autonumbered ? null : this.document_number,
        document_issue_date: this.document_issue_date,
        warehouse_id: this.selectedWarehouse?.id,
        supplier: {
          id: this.supplier.id || null,
          name: this.supplier.name,
          trade_name: this.supplier.trade_name,
          tax_registration_number: this.supplier.tax_registration_number,
          tax_registration_office_id: this.supplier.tax_registration_office_id,
          country_id: this.supplier.country_id || 1,
          city: this.supplier.city,
          address: this.supplier.address,
          zipcode: this.supplier.zipcode,
          telephone: this.supplier.telephone,
          fax: this.supplier.fax,
          mobile: this.supplier.mobile,
          email: this.supplier.email,
          jobtitle: this.supplier.jobtitle,
        },
        payment_type_id: this.selectedPaymentType?.id,
        comments: this.comments,

        tax_withholding_percentage: this.tax_withholding_percentage,
        tax_withholding_amount: this.calculatedTaxWithholdingAmount,
        stamp_duty_percentage: this.stamp_duty_percentage,
        stamp_duty_amount: this.calculatedStampDutyAmount,
        efka_percentage: this.efka_percentage,
        efka_amount: this.calculatedEfkaAmount,

        productLines: this.items.map((i) => ({
          product_id: i.product_id || null,
          override_product_description: i.override_product_description,
          override_product_measurement_unit_id: i.override_product_measurement_unit_id,
          quantity: i.quantity,
          unit_price: i.unit_price,
          discount_percentage: i.discount_percentage,
          discount: i.discount,
          total_price: i.total_price,
          expense_classification_type: i.expense_classification_type,
          expense_classification_category: i.expense_classification_category,
        })),
      };

      const request = this.isEditMode
        ? axios.patch(`/api/documents/acquisition/${this.$route.params.id}`, requestBody)
        : axios.post("/api/documents/acquisition", requestBody);

      request
        .then(() => {
          if (this.$swal) {
            this.$swal({
              icon: "success",
              title: "Επιτυχία!",
              text: this.isEditMode ? "Ο Τίτλος Κτήσης ενημερώθηκε επιτυχώς." : "Ο Τίτλος Κτήσης εκδόθηκε επιτυχώς.",
              timer: 1500,
              showConfirmButton: false,
            });
          }
          this.$router.push({ name: "acquisitiondocuments" });
        })
        .catch((error) => {
          if (error.response?.status === 422 && error.response?.data?.errors) {
            this.allerrors = error.response.data.errors;
            const messages = Object.values(error.response.data.errors).flat().join("<br>");
            if (this.$swal) {
              this.$swal({ icon: "error", title: "Σφάλμα επικύρωσης", html: messages });
            }
          } else if (error.response?.data?.message) {
            if (this.$swal) {
              this.$swal({ icon: "error", title: "Σφάλμα", text: error.response.data.message });
            }
          }
        })
        .finally(() => {
          this.saveDocumentButtonDisabled = false;
        });
    },
  },
};
</script>

<style scoped>
.v-card {
  max-width: 900px;
}
</style>
